import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import { NavIcon } from '../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import {
  qPatientWorkspaceSettings,
  mapPatientWorkspaceSettingsToUI,
  PATIENT_WORKSPACE_DEFAULTS,
  qAppConfig,
  getClinicClassification,
  buildClinicWorkspacePreset,
  applyPresetToWorkspaceSettings,
  PRIMARY_CLINIC_TYPE_LABELS,
  type AppConfigRow,
  type PatientWorkspaceSettingsRow,
  type PatientWorkspaceSettingsUI,
  type WorkspaceMode,
  type DefaultSidebarState,
  type TreatmentRecordDrawerWidth,
  type PatientOverviewDisplay,
} from '@helios/data-access';
import { persistPatientWorkspaceSettings } from './persistPatientWorkspaceSettings';

// ─── UI form state ───

type FormState = Omit<PatientWorkspaceSettingsUI, 'id'>;

const DEFAULTS: FormState = { ...PATIENT_WORKSPACE_DEFAULTS };

// ─── Option lists ───

const WORKSPACE_MODE_OPTIONS: { value: WorkspaceMode; label: string; description: string }[] = [
  {
    value: 'treatment_record',
    label: 'Treatment Record',
    description: 'Simpler patient experience focused on treatment documentation.',
  },
  {
    value: 'full_workspace',
    label: 'Full Workspace',
    description: 'Richer patient workspace with more surfaces and navigation depth.',
  },
];

const SIDEBAR_STATE_OPTIONS: { value: DefaultSidebarState; label: string }[] = [
  { value: 'expanded', label: 'Expanded' },
  { value: 'collapsed', label: 'Collapsed' },
];

const TREATMENT_DRAWER_WIDTH_OPTIONS: { value: TreatmentRecordDrawerWidth; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'wide', label: 'Wide' },
  { value: 'expanded', label: 'Expanded' },
];

// PRD.226 — wording per §9.3. "Sidebar only" vs "Sidebar + Content Body"
// makes it obvious that Patient Overview is still in the sidebar either way.
const PATIENT_OVERVIEW_DISPLAY_OPTIONS: { value: PatientOverviewDisplay; label: string }[] = [
  { value: 'sidebar_only', label: 'Sidebar only' },
  { value: 'sidebar_and_body', label: 'Sidebar + Content Body' },
];

interface ToggleSpec {
  key: keyof FormState;
  title: string;
  description: string;
}

const SIDEBAR_TOGGLES: ToggleSpec[] = [
  {
    key: 'sidebarShowOverview',
    title: 'Overview',
    description: 'Patient summary, allergies, last vitals, billing.',
  },
  {
    key: 'sidebarShowIntakeForm',
    title: 'Intake Form',
    description: 'Show the intake form section in the patient sidebar.',
  },
  { key: 'sidebarShowVitals', title: 'Vitals', description: 'Recent vitals readings.' },
  {
    key: 'sidebarShowAllergies',
    title: 'Allergies',
    description: 'Allergies, medications, and medical history.',
  },
  { key: 'sidebarShowVisits', title: 'Visits', description: 'Encounters / visit timeline.' },
  {
    key: 'sidebarShowTreatmentRecord',
    title: 'Treatment Record',
    description: 'Procedures and payments recorded for the patient.',
  },
  {
    key: 'sidebarShowContact',
    title: 'Contact',
    description: 'Contact details and emergency contacts.',
  },
  // PRD.203 — Patient Pharmacy as a first-class sidebar tab.
  {
    key: 'sidebarShowPharmacy',
    title: 'Pharmacy',
    description: 'Patient-wide medicines ready to dispense and dispense history.',
  },
  // PRD.262 — Patient Packages sidebar tab (ownership + redemption).
  {
    key: 'sidebarShowPackages',
    title: 'Packages',
    description: 'Patient-owned packages, benefit balances, and redemption.',
  },
  // PRD.286 — Files & Images master file repository sidebar tab.
  {
    key: 'sidebarShowFilesImages',
    title: 'Files & Images',
    description: 'Patient-wide master repository for uploaded files and images.',
  },
];

const CONSULT_TOGGLES: ToggleSpec[] = [
  {
    key: 'consultShowSummary',
    title: 'Summary',
    description: 'Visit details and chief complaint.',
  },
  {
    key: 'consultShowChiefComplaint',
    title: 'Chief Complaint',
    description: "The patient's stated reason for the visit.",
  },
  { key: 'consultShowVitals', title: 'Vitals', description: 'Vitals captured during the visit.' },
  { key: 'consultShowCharting', title: 'Charting', description: 'Dental / clinical charting.' },
  { key: 'consultShowNotes', title: 'Notes', description: 'Clinical notes for the visit.' },
  {
    key: 'consultShowSymptoms',
    title: 'Symptoms',
    description: 'Structured symptom entry for the visit.',
  },
  {
    key: 'consultShowDiagnoses',
    title: 'Diagnosis',
    description: 'Structured diagnosis entry for the visit.',
  },
  {
    key: 'consultShowPrescriptions',
    title: 'Prescriptions',
    description: 'Prescriptions issued during the visit.',
  },
  {
    key: 'consultShowTreatmentPlan',
    title: 'Treatment Plan',
    description: 'Recommended treatment plan.',
  },
  { key: 'consultShowForms', title: 'Forms', description: 'Forms attached to the visit.' },
  {
    key: 'consultShowFiles',
    title: 'Files',
    description: 'Files and images uploaded for the visit.',
  },
  {
    key: 'consultShowInvoices',
    title: 'Invoices',
    description: 'Invoices generated for the visit.',
  },
  {
    key: 'consultShowPharmacy',
    title: 'Pharmacy',
    description: 'Dispense medicines from paid invoice lines during the visit.',
  },
  {
    key: 'consultShowPayments',
    title: 'Payments',
    description: 'Payments collected for the visit.',
  },
];

// ─── Toggle ───

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 'var(--radius-full)',
        background: checked ? 'var(--color-primary)' : 'var(--border)',
        border: 'none',
        padding: 2,
        cursor: 'pointer',
        transition: 'background 150ms ease',
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#ffffff',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
          transition: 'transform 150ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
        }}
      />
    </button>
  );
}

// ─── Card ───

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-6)',
      }}
    >
      {children}
    </div>
  );
}

function SettingToggleRow({
  title,
  description,
  checked,
  onChange,
  divider = true,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4) 0',
        borderBottom: divider ? '1px solid var(--border)' : undefined,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
          {description}
        </div>
      </div>
      <Toggle checked={checked} onChange={onChange} />
    </div>
  );
}

function SettingSelectRow<T extends string>({
  title,
  description,
  value,
  options,
  onChange,
  divider = true,
}: {
  title: string;
  description: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  divider?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4) 0',
        borderBottom: divider ? '1px solid var(--border)' : undefined,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            color: 'var(--text)',
          }}
        >
          {title}
        </div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
          {description}
        </div>
      </div>
      <select value={value} onChange={(e) => onChange(e.target.value as T)} style={selectStyle}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ModeRadioRow({
  value,
  onChange,
}: {
  value: WorkspaceMode;
  onChange: (v: WorkspaceMode) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      {WORKSPACE_MODE_OPTIONS.map((opt) => {
        const selected = opt.value === value;
        return (
          <label
            key={opt.value}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--sp-3)',
              padding: 'var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              border: `1px solid ${selected ? 'var(--color-primary)' : 'var(--border)'}`,
              background: selected ? 'var(--color-primary-subtle, #fffbeb)' : 'var(--bg-surface)',
              cursor: 'pointer',
              transition: 'border-color 150ms, background 150ms',
            }}
          >
            <input
              type="radio"
              name="workspace-mode"
              checked={selected}
              onChange={() => onChange(opt.value)}
              style={{ marginTop: 3, accentColor: 'var(--color-primary)' }}
            />
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                }}
              >
                {opt.label}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                {opt.description}
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
}

// ─── Page ───

export function PatientWorkspaceSettingsPage() {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const { data: rows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  // PRD.282 — the clinic classification drives the "Apply Clinic Defaults"
  // preset. Read-only here; clinic type is edited in Clinic Settings.
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const classification = useMemo(
    () => getClinicClassification(appConfigRows[0] ?? null),
    [appConfigRows],
  );

  const [settings, setSettings] = useState<FormState>({ ...DEFAULTS });
  const [savedSnapshot, setSavedSnapshot] = useState<FormState>({ ...DEFAULTS });
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // PRD.282 — confirmation before replacing tab selections with clinic defaults.
  const [applyDefaultsConfirmOpen, setApplyDefaultsConfirmOpen] = useState(false);
  const [applyingDefaults, setApplyingDefaults] = useState(false);

  // Hydrate form from DB when row arrives
  useEffect(() => {
    if (loaded || rows.length === 0) return;
    const ui = mapPatientWorkspaceSettingsToUI(rows[0]);
    const next: FormState = {
      workspaceMode: ui.workspaceMode,
      showTreatmentRecord: ui.showTreatmentRecord,
      showPatientTimeline: ui.showPatientTimeline,
      defaultSidebarState: ui.defaultSidebarState,
      sidebarShowOverview: ui.sidebarShowOverview,
      sidebarShowIntakeForm: ui.sidebarShowIntakeForm,
      sidebarShowVitals: ui.sidebarShowVitals,
      sidebarShowAllergies: ui.sidebarShowAllergies,
      sidebarShowVisits: ui.sidebarShowVisits,
      sidebarShowTreatmentRecord: ui.sidebarShowTreatmentRecord,
      sidebarShowContact: ui.sidebarShowContact,
      sidebarShowPharmacy: ui.sidebarShowPharmacy,
      sidebarShowPackages: ui.sidebarShowPackages,
      sidebarShowFilesImages: ui.sidebarShowFilesImages,
      consultShowSummary: ui.consultShowSummary,
      consultShowVitals: ui.consultShowVitals,
      consultShowCharting: ui.consultShowCharting,
      consultShowNotes: ui.consultShowNotes,
      consultShowPrescriptions: ui.consultShowPrescriptions,
      consultShowTreatmentPlan: ui.consultShowTreatmentPlan,
      consultShowForms: ui.consultShowForms,
      consultShowFiles: ui.consultShowFiles,
      consultShowInvoices: ui.consultShowInvoices,
      consultShowPayments: ui.consultShowPayments,
      consultShowChiefComplaint: ui.consultShowChiefComplaint,
      consultShowSymptoms: ui.consultShowSymptoms,
      consultShowDiagnoses: ui.consultShowDiagnoses,
      consultShowPharmacy: ui.consultShowPharmacy,
      treatmentRecordDrawerWidth: ui.treatmentRecordDrawerWidth,
      patientOverviewDisplay: ui.patientOverviewDisplay,
      queueActionPayEnabled: ui.queueActionPayEnabled,
      queueActionDispenseEnabled: ui.queueActionDispenseEnabled,
    };
    setSettings(next);
    setSavedSnapshot(next);
    setLoaded(true);
  }, [rows, loaded]);

  const isDirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(savedSnapshot),
    [settings, savedSnapshot],
  );

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }, []);

  const persist = useCallback(
    async (values: FormState) => {
      if (!db) return;
      // PRD.282 — single grouped write, shared with Clinic Settings so preset
      // application and manual edits go through the identical path.
      await persistPatientWorkspaceSettings(db, {
        orgId,
        existing: rows[0] ? { id: rows[0].id } : null,
        values,
      });
    },
    [db, orgId, rows],
  );

  const handleSave = useCallback(async () => {
    if (!db || saving) return;
    setSaving(true);
    try {
      await persist(settings);
      setSavedSnapshot({ ...settings });
      showToast('Settings saved');
    } catch (err) {
      console.error('[PatientWorkspaceSettings] save error:', err);
      showToast('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [db, saving, persist, settings, showToast]);

  // PRD.282 — "Apply Clinic Defaults" replaces the Sidebar/Visit Note tab
  // selections with the preset for the clinic's saved type + specialties. It
  // requires confirmation and preserves non-tab settings. The clinic type is
  // never recalculated on load — this only runs on explicit user action.
  const confirmApplyClinicDefaults = useCallback(async () => {
    if (!db) return;
    setApplyDefaultsConfirmOpen(false);
    setApplyingDefaults(true);
    const preset = buildClinicWorkspacePreset(classification);
    const nextUi = applyPresetToWorkspaceSettings(settings, preset);
    try {
      await persist(nextUi);
      setSettings(nextUi);
      setSavedSnapshot(nextUi);
      showToast('Clinic defaults applied');
    } catch (err) {
      console.error('[PatientWorkspaceSettings] apply clinic defaults error:', err);
      // PRD §Error Handling — the single grouped write is atomic, so the saved
      // configuration is intact. Keep the current form untouched and let the
      // admin retry.
      showToast(
        'Clinic defaults could not be applied. Your previous workspace settings were kept.',
      );
    } finally {
      setApplyingDefaults(false);
    }
  }, [db, classification, settings, persist, showToast]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page Header */}
      <div
        style={{
          height: 'var(--clinic-header-height)',
          padding: '0 var(--sp-6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--font-size-xl2)',
              fontWeight: 'var(--font-weight-semi)',
              lineHeight: 'var(--line-height-xl2)',
              letterSpacing: '-0.44px',
              color: 'var(--text)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
            }}
          >
            Patient Workspace
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 22,
                padding: '0 var(--sp-2)',
                borderRadius: 'var(--radius-xs)',
                background: '#fef3c7',
                color: '#92400e',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              Admin only
            </span>
          </h1>
          <p
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              margin: '2px 0 0',
            }}
          >
            Configure how the patient workspace behaves and which surfaces are shown
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          {/* PRD.282 — was "Reset to Default"; now applies the clinic-type preset. */}
          <button
            type="button"
            onClick={() => setApplyDefaultsConfirmOpen(true)}
            disabled={applyingDefaults}
            title="Replace the tab selections with defaults for this clinic type"
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: applyingDefaults ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: 'var(--sp-2) var(--sp-3)',
            }}
          >
            <NavIcon name="refresh-cw" />
            Apply Clinic Defaults
          </button>
          <Button variant="gradient" size="md" onClick={handleSave} disabled={!isDirty || saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* PRD.282 §2.2 — confirmation before replacing tab selections. */}
      <Modal
        open={applyDefaultsConfirmOpen}
        onOpenChange={setApplyDefaultsConfirmOpen}
        title="Apply clinic defaults?"
        description={`This will replace the current Patient Sidebar and Visit Note tab selections with defaults for this clinic type (${PRIMARY_CLINIC_TYPE_LABELS[classification.primaryClinicType]}). You can modify them afterward.`}
        maxWidth={460}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
            <Button variant="ghost" size="md" onClick={() => setApplyDefaultsConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="gradient" size="md" onClick={confirmApplyClinicDefaults}>
              Apply Defaults
            </Button>
          </div>
        }
      >
        {null}
      </Modal>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--sp-6)' }}>
        {toast && (
          <div
            style={{
              marginBottom: 'var(--sp-4)',
              padding: 'var(--sp-3) var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: '#dcfce7',
              color: '#15803d',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              maxWidth: 720,
            }}
          >
            {toast}
          </div>
        )}

        {/* PRD.282 §2.1 — informational notice about clinic-type defaults. */}
        <div
          style={{
            maxWidth: 720,
            marginBottom: 'var(--sp-4)',
            padding: 'var(--sp-3) var(--sp-4)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--bg-surface-2, #f1f5f9)',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--sp-2)',
          }}
        >
          <NavIcon name="info" />
          Workspace defaults are based on the clinic type (
          {PRIMARY_CLINIC_TYPE_LABELS[classification.primaryClinicType]}). Administrators can
          customize all tabs below.
        </div>

        <div
          style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}
        >
          {/* Workspace Mode */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-3)' }}>
              <h2 style={cardTitleStyle}>Workspace Mode</h2>
              <p style={cardDescStyle}>
                The clinic&rsquo;s preferred patient workspace complexity.
              </p>
            </div>
            <ModeRadioRow
              value={settings.workspaceMode}
              onChange={(v) => update('workspaceMode', v)}
            />
          </Card>

          {/* Major visibility */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Workspace Visibility</h2>
              <p style={cardDescStyle}>
                Show or hide major patient surfaces independently of mode.
              </p>
            </div>
            <SettingToggleRow
              title="Show Treatment Record"
              description="Surface the Treatment Record alongside Visit Notes."
              checked={settings.showTreatmentRecord}
              onChange={(v) => update('showTreatmentRecord', v)}
            />
            <SettingToggleRow
              title="Show Patient Timeline"
              description="Show the patient timeline of encounters and activity."
              checked={settings.showPatientTimeline}
              onChange={(v) => update('showPatientTimeline', v)}
              divider={false}
            />
          </Card>

          {/* Default sidebar state */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Default Sidebar State</h2>
              <p style={cardDescStyle}>
                How the patient sidebar opens by default when entering a patient.
              </p>
            </div>
            <SettingSelectRow
              title="Default state"
              description="Expanded shows full context; Collapsed saves space by default."
              value={settings.defaultSidebarState}
              options={SIDEBAR_STATE_OPTIONS}
              onChange={(v) => update('defaultSidebarState', v as DefaultSidebarState)}
              divider={false}
            />
          </Card>

          {/* Patient Overview Display (PRD.226) */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Patient Overview Display</h2>
              <p style={cardDescStyle}>
                Choose whether Patient Overview appears in the main content body in addition to the
                sidebar. The sidebar Patient Overview is always available regardless.
              </p>
            </div>
            <SettingSelectRow
              title="Placement"
              description="Sidebar only keeps the content body focused on the working surface. Sidebar + Content Body also shows a richer overview at the top of the patient page."
              value={settings.patientOverviewDisplay}
              options={PATIENT_OVERVIEW_DISPLAY_OPTIONS}
              onChange={(v) => update('patientOverviewDisplay', v as PatientOverviewDisplay)}
              divider={false}
            />
          </Card>

          {/* Sidebar tab visibility */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Patient Sidebar Tabs</h2>
              <p style={cardDescStyle}>Choose which tabs appear in the patient sidebar.</p>
            </div>
            {SIDEBAR_TOGGLES.map((spec, idx) => (
              <SettingToggleRow
                key={spec.key}
                title={spec.title}
                description={spec.description}
                checked={settings[spec.key] as boolean}
                onChange={(v) => update(spec.key, v as never)}
                divider={idx < SIDEBAR_TOGGLES.length - 1}
              />
            ))}
          </Card>

          {/* Consultation tab visibility */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Visit Note Tabs</h2>
              <p style={cardDescStyle}>
                Choose which tabs appear inside a patient visit / consultation.
              </p>
            </div>
            {CONSULT_TOGGLES.map((spec, idx) => (
              <SettingToggleRow
                key={spec.key}
                title={spec.title}
                description={spec.description}
                checked={settings[spec.key] as boolean}
                onChange={(v) => update(spec.key, v as never)}
                divider={idx < CONSULT_TOGGLES.length - 1}
              />
            ))}
          </Card>

          {/* Treatment Record Workspace (PRD.182) */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Treatment Record Workspace</h2>
              <p style={cardDescStyle}>
                Behavior settings used when Workspace Mode is set to Treatment Record.
              </p>
            </div>
            <SettingSelectRow
              title="Right drawer width"
              description="Drawer size used by Charting & Files and Invoice flows in Treatment Record mode."
              value={settings.treatmentRecordDrawerWidth}
              options={TREATMENT_DRAWER_WIDTH_OPTIONS}
              onChange={(v) =>
                update('treatmentRecordDrawerWidth', v as TreatmentRecordDrawerWidth)
              }
              divider={false}
            />
          </Card>

          {/* Queue Actions (PRD.237) — controls which optional Queue
              actions are surfaced on the Queue page. Billing and
              dispense still reuse their existing workflows; these
              toggles only control whether the Queue row exposes those
              entry points. Disabled toggles hide the chevron in Queue
              without touching the underlying billing/dispense logic. */}
          <Card>
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <h2 style={cardTitleStyle}>Queue Actions</h2>
              <p style={cardDescStyle}>
                Choose which optional actions appear on Queue rows. Disabling an action hides it
                from Queue without changing the underlying billing or dispense workflows.
              </p>
            </div>
            <SettingToggleRow
              title="Pay"
              description="Show the Pay/Paid action on Queue rows. Opens the existing Billing drawer."
              checked={settings.queueActionPayEnabled}
              onChange={(v) => update('queueActionPayEnabled', v)}
              divider
            />
            <SettingToggleRow
              title="Dispense"
              description="Show the Dispense action on Queue rows when items are ready. Opens the existing Pharmacy drawer."
              checked={settings.queueActionDispenseEnabled}
              onChange={(v) => update('queueActionDispenseEnabled', v)}
              divider={false}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─── Shared styles ───

const cardTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semi)',
  lineHeight: 'var(--line-height-lg)',
  color: 'var(--text)',
  margin: 0,
};

const cardDescStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
  margin: '2px 0 0',
};

const selectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--sp-8) 0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%2364748b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  minWidth: 160,
};
